import * as React from 'react';

import { useCanScrollY } from '@hooks/useCanScrollY';
import { cn } from '@lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  KEYBOARD_SHEET_MAX_HEIGHT,
} from './drawer';
import { Button } from './button';
import { INK_SOLID_BUTTON_CLASS } from './buttonVariants';
import { CAPPED_SHEET_BODY_CLASS, scrollFocusedIntoView } from './cappedSheet';
import { HELD_BUTTON_CLASS, ignoreRepeatedEnter } from './heldButton';

export interface ConfirmDeleteDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: string;
  description?: React.ReactNode;

  confirmLabel?: string;
  cancelLabel?: string;

  isConfirming?: boolean;
  onConfirm: () => void;

  /**
   * Which button is the filled, primary one. `confirm` (default): an outline Cancel over a red
   * Delete. `cancel`: a filled `mb-teal-ink` Cancel over an outlined red Delete, for a delete with
   * no undo where the safe choice should be the obvious one (the partnership delete, TECH-86 B3).
   * With `cancel`, focus also moves onto Cancel as the sheet opens (vaul leaves it outside the
   * sheet); with `confirm` focus is left as vaul leaves it.
   */
  primaryAction?: 'confirm' | 'cancel';

  /**
   * Radix's close autofocus, for a sheet opened from code (no Trigger to return to): call
   * `preventDefault()` and focus the control that opened it. Unset: Radix's default.
   */
  onCloseAutoFocus?: (event: Event) => void;

  /**
   * Stacks over the drawer that opened it (a confirm over its parent form): a lighter dimmer in
   * place of a second full scrim. See DrawerContent's `nested`.
   */
  nested?: boolean;
}

// Both buttons are 44px tall, the touch floor, whichever one is filled.
const BUTTON_CLASS = 'h-11 rounded-xl';
const CANCEL_PRIMARY_CLASS = `text-base ${INK_SOLID_BUTTON_CLASS} focus-visible:ring-mb-teal-ink focus-visible:ring-offset-2`;
const CONFIRM_SECONDARY_CLASS =
  'text-base border-destructive text-destructive hover:bg-destructive/5 hover:text-destructive focus-visible:ring-destructive focus-visible:ring-offset-2';

export const ConfirmDeleteDrawer: React.FC<ConfirmDeleteDrawerProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  isConfirming = false,
  onConfirm,
  primaryAction = 'confirm',
  nested,
  onCloseAutoFocus,
}) => {
  const cancelIsPrimary = primaryAction === 'cancel';
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  // Names the header's region. Not the title's own id: Radix names the dialog by that one.
  const titleId = React.useId();
  const [measureHeader, titleCanScroll] = useCanScrollY();
  const [headerHasFocus, setHeaderHasFocus] = React.useState(false);
  // A header that leaves the page (the drawer closed from under the keyboard) holds no focus, and
  // no blur says so: the hold goes with it, so the next opening starts from the title's size alone.
  const headerRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      const stopMeasuring = measureHeader(node);
      if (node === null) return undefined;
      return () => {
        stopMeasuring?.();
        setHeaderHasFocus(false);
      };
    },
    [measureHeader],
  );
  // A Tab stop only while there is something to scroll to: a title that fits is no stop, and a
  // screen reader does not hear it twice. One that stops overflowing under focus stays a stop until
  // focus leaves it, so the focus trap never loses the keyboard on it.
  const headerIsStop = titleCanScroll || headerHasFocus;
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        nested={nested}
        // The keyboard sheet's cap, without its body lock (nothing here takes a keyboard): a title
        // or description too long for the screen, such as a long event name at a large text size,
        // scrolls between the handle and the buttons instead of pushing the sheet's top off it.
        className={cn('px-4 pb-safe', KEYBOARD_SHEET_MAX_HEIGHT)}
        onOpenAutoFocus={
          cancelIsPrimary
            ? event => {
                event.preventDefault();
                cancelRef.current?.focus({ preventScroll: true });
              }
            : undefined
        }
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {/* Where even the buttons don't fit under the cap (a phone on its side at a large text
            size), everything under the handle scrolls, so both can still be reached. The header
            keeps its floor, so the title stays readable, and at its end hands a swipe on to this
            scroller, so the buttons can be swiped up too (CAPPED_SHEET_BODY_CLASS's docblock). */}
        <div className={CAPPED_SHEET_BODY_CLASS} onFocus={scrollFocusedIntoView}>
          {/* While the title overflows, a keyboard scrolls the header once it takes focus (WCAG
              2.1.1), so it is a region named by the title. Its focus outline is the house ring's
              colour and width, drawn inside it and rounded as the buttons are: one drawn outside
              would be cut off at the body's edges, which the header meets. An outline, not a ring:
              it paints over the title where the region cuts it, and forced colors keep it (they
              drop a ring's shadow). Inset by the body's own room, so the outline clears the
              title. */}
          <DrawerHeader
            ref={headerRef}
            role={headerIsStop ? 'region' : undefined}
            aria-labelledby={headerIsStop ? titleId : undefined}
            tabIndex={headerIsStop ? 0 : undefined}
            onFocus={event => {
              if (event.target === event.currentTarget) setHeaderHasFocus(true);
            }}
            onBlur={event => {
              if (event.target === event.currentTarget) setHeaderHasFocus(false);
            }}
            className="-mx-1 min-h-16 overflow-y-auto rounded-xl px-1 text-left focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mb-teal-ink"
          >
            <DrawerTitle>
              <span id={titleId}>{title}</span>
            </DrawerTitle>
            {/* A description can name a value, such as a template's key: it wraps rather than overflow. */}
            {description ? (
              <DrawerDescription className="wrap-anywhere">{description}</DrawerDescription>
            ) : null}
          </DrawerHeader>

          {/* While it deletes, both buttons hold rather than turn disabled: a browser takes focus off
              a button that turns disabled, and in an open dialog it falls to the page under it. The
              drawer itself still closes. Delete keeps focus through a refusal, so it takes a held
              Enter as one press. */}
          <DrawerFooter className="shrink-0 px-0">
            <Button
              ref={cancelRef}
              type="button"
              onClick={() => {
                if (isConfirming) return;
                onOpenChange(false);
              }}
              variant={cancelIsPrimary ? 'default' : 'outline'}
              aria-disabled={isConfirming}
              className={cn(
                BUTTON_CLASS,
                cancelIsPrimary && CANCEL_PRIMARY_CLASS,
                HELD_BUTTON_CLASS,
              )}
            >
              {cancelLabel}
            </Button>
            <Button
              type="button"
              variant={cancelIsPrimary ? 'outline' : 'destructive'}
              onClick={() => {
                if (isConfirming) return;
                onConfirm();
              }}
              onKeyDown={ignoreRepeatedEnter}
              aria-disabled={isConfirming}
              className={cn(
                BUTTON_CLASS,
                cancelIsPrimary && CONFIRM_SECONDARY_CLASS,
                HELD_BUTTON_CLASS,
              )}
            >
              {isConfirming ? 'Deleting…' : confirmLabel}
            </Button>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
